import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Sidebar from '../components/Sidebar';
import { ticketService } from '../services/ticketService';
import { expenseService } from '../services/expenseService';
import { propertyService } from '../services/propertyService';
import { tenantService } from '../services/tenantService';
import { authService } from '../services/authService';
import { bidService } from '../services/bidService';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, ArcElement } from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, ArcElement);

const AdminReports = () => {
  const { currentUser, userData } = useAuth();
  const [loading, setLoading] = useState(true);
  const [selectedTimeframe, setSelectedTimeframe] = useState('month');
  const [reportData, setReportData] = useState({
    userGrowth: [],
    revenueData: [],
    ticketMetrics: [],
    propertyStats: [],
    topPerformers: []
  });
  const [exportFormat, setExportFormat] = useState('pdf');
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (currentUser && userData) {
      loadReportData();
    }
  }, [currentUser, userData, selectedTimeframe]);

  const loadReportData = async () => {
    try {
      setLoading(true);
      console.log('Loading report data for timeframe:', selectedTimeframe);
      
      // Load all data for reports
      const [users, properties, tickets, expenses, bids] = await Promise.all([
        authService.getAllUsers(),
        propertyService.getAllProperties(),
        ticketService.getAllTickets(),
        expenseService.getAllExpenses(),
        bidService.getAllBids()
      ]);

      console.log('Raw data loaded:', {
        users: users.length,
        properties: properties.length,
        tickets: tickets.length,
        expenses: expenses.length,
        bids: bids.length
      });

      // Process data for different timeframes
      const processedData = processDataForTimeframe(users, properties, tickets, expenses, bids);
      console.log('Processed report data:', processedData);
      
      setReportData(processedData);
      setLoading(false);
    } catch (error) {
      console.error('Error loading report data:', error);
      // Set default empty data structure
      setReportData({
        userGrowth: [],
        revenueData: [],
        ticketMetrics: {
          total: 0,
          open: 0,
          inProgress: 0,
          completed: 0,
          closed: 0,
          averageResolutionTime: 0
        },
        propertyStats: {
          total: 0,
          occupied: 0,
          vacant: 0,
          maintenance: 0,
          averageRent: 0
        },
        topPerformers: []
      });
      setLoading(false);
    }
  };

  const processDataForTimeframe = (users, properties, tickets, expenses, bids) => {
    const now = new Date();
    let startDate = new Date();
    
    switch (selectedTimeframe) {
      case 'week':
        startDate.setDate(now.getDate() - 7);
        break;
      case 'month':
        startDate.setMonth(now.getMonth() - 1);
        break;
      case 'quarter':
        startDate.setMonth(now.getMonth() - 3);
        break;
      case 'year':
        startDate.setFullYear(now.getFullYear() - 1);
        break;
      default:
        startDate = new Date(0); // All time
    }

    // Helper function to safely convert Firebase Timestamp to Date
    const safeDateConversion = (timestamp) => {
      if (!timestamp) return null;
      if (timestamp.toDate && typeof timestamp.toDate === 'function') {
        return timestamp.toDate();
      }
      return new Date(timestamp);
    };

    // Filter data by timeframe with safe date conversion
    const filteredUsers = users.filter(user => {
      const userDate = safeDateConversion(user.createdAt);
      return userDate && userDate >= startDate;
    });
    
    const filteredProperties = properties.filter(prop => {
      const propDate = safeDateConversion(prop.createdAt);
      return propDate && propDate >= startDate;
    });
    
    const filteredTickets = tickets.filter(ticket => {
      const ticketDate = safeDateConversion(ticket.createdAt);
      return ticketDate && ticketDate >= startDate;
    });
    
    const filteredExpenses = expenses.filter(expense => {
      const expenseDate = safeDateConversion(expense.createdAt);
      return expenseDate && expenseDate >= startDate;
    });
    
    const filteredBids = bids.filter(bid => {
      const bidDate = safeDateConversion(bid.createdAt);
      return bidDate && bidDate >= startDate;
    });

    // User growth data
    const userGrowth = generateTimeSeriesData(filteredUsers, 'createdAt', 'users');

    // Revenue data
    const revenueData = generateTimeSeriesData(filteredExpenses, 'createdAt', 'revenue');

    // Ticket metrics
    const ticketMetrics = {
      total: filteredTickets.length,
      open: filteredTickets.filter(t => t.status === 'open').length,
      inProgress: filteredTickets.filter(t => t.status === 'in-progress').length,
      completed: filteredTickets.filter(t => t.status === 'completed').length,
      closed: filteredTickets.filter(t => t.status === 'closed').length,
      averageResolutionTime: calculateAverageResolutionTime(filteredTickets)
    };

    // Property statistics
    const propertyStats = {
      total: filteredProperties.length,
      occupied: filteredProperties.filter(p => p.status === 'occupied').length,
      vacant: filteredProperties.filter(p => p.status === 'vacant').length,
      maintenance: filteredProperties.filter(p => p.status === 'maintenance').length,
      averageRent: calculateAverageRent(filteredProperties)
    };

    // Top performers
    const topPerformers = getTopPerformers(filteredTickets, filteredExpenses, filteredBids);

    return {
      userGrowth,
      revenueData,
      ticketMetrics,
      propertyStats,
      topPerformers
    };
  };

  const generateTimeSeriesData = (data, dateField, valueField) => {
    const grouped = {};
    
    data.forEach(item => {
      // Safely convert Firebase Timestamp to Date
      let date;
      if (item[dateField] && item[dateField].toDate && typeof item[dateField].toDate === 'function') {
        date = item[dateField].toDate();
      } else {
        date = new Date(item[dateField]);
      }
      
      if (isNaN(date.getTime())) {
        console.warn('Invalid date for item:', item);
        return; // Skip items with invalid dates
      }
      
      const key = date.toISOString().split('T')[0];
      
      if (!grouped[key]) {
        grouped[key] = 0;
      }
      
      if (valueField === 'users') {
        grouped[key]++;
      } else if (valueField === 'revenue') {
        grouped[key] += item.amount || 0;
      }
    });

    return Object.entries(grouped)
      .map(([date, value]) => ({ date, value }))
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  const calculateAverageResolutionTime = (tickets) => {
    const completedTickets = tickets.filter(t => t.status === 'completed' && t.completedAt);
    
    if (completedTickets.length === 0) return 0;
    
    const totalTime = completedTickets.reduce((sum, ticket) => {
      const created = safeDateConversion(ticket.createdAt);
      const completed = safeDateConversion(ticket.completedAt);
      
      if (!created || !completed) return sum;
      
      return sum + (completed - created);
    }, 0);
    
    return Math.round(totalTime / completedTickets.length / (1000 * 60 * 60 * 24)); // Days
  };

  // Helper function to safely convert Firebase Timestamp to Date
  const safeDateConversion = (timestamp) => {
    if (!timestamp) return null;
    if (timestamp.toDate && typeof timestamp.toDate === 'function') {
      return timestamp.toDate();
    }
    return new Date(timestamp);
  };

  const calculateAverageRent = (properties) => {
    const propertiesWithRent = properties.filter(p => p.monthlyRent);
    
    if (propertiesWithRent.length === 0) return 0;
    
    const totalRent = propertiesWithRent.reduce((sum, prop) => sum + (prop.monthlyRent || 0), 0);
    return Math.round(totalRent / propertiesWithRent.length);
  };

  const getTopPerformers = (tickets, expenses, bids) => {
    // Top service providers by completed tickets
    const providerStats = {};
    
    tickets.forEach(ticket => {
      if (ticket.status === 'completed' && ticket.assignedTo) {
        if (!providerStats[ticket.assignedTo]) {
          providerStats[ticket.assignedTo] = { completed: 0, totalExpenses: 0 };
        }
        providerStats[ticket.assignedTo].completed++;
      }
    });

    expenses.forEach(expense => {
      if (expense.assignedTo && providerStats[expense.assignedTo]) {
        providerStats[expense.assignedTo].totalExpenses += expense.amount || 0;
      }
    });

    return Object.entries(providerStats)
      .map(([providerId, stats]) => ({
        providerId,
        completedTickets: stats.completed,
        totalExpenses: stats.totalExpenses
      }))
      .sort((a, b) => b.completedTickets - a.completedTickets)
      .slice(0, 5);
  };

  const exportReport = async () => {
    try {
      setExporting(true);
      console.log('Exporting report in format:', exportFormat);
      
      // Create report data
      const reportContent = {
        timeframe: selectedTimeframe,
        generatedAt: new Date().toISOString(),
        data: reportData
      };

      if (exportFormat === 'csv') {
        exportToCSV(reportContent);
      } else if (exportFormat === 'excel') {
        exportToExcel(reportContent);
      } else {
        exportToPDF(reportContent);
      }
      
      setExporting(false);
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export report. Please try again.');
      setExporting(false);
    }
  };

  const exportToCSV = (data) => {
    try {
      // Convert data to CSV format
      const csvContent = convertToCSV(data);
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `admin-report-${selectedTimeframe}-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      console.log('CSV export completed successfully');
    } catch (error) {
      console.error('CSV export error:', error);
      alert('Failed to export CSV. Please try again.');
    }
  };

  const exportToExcel = (data) => {
    try {
      // For now, export as CSV with .xls extension
      const csvContent = convertToCSV(data);
      const blob = new Blob([csvContent], { type: 'application/vnd.ms-excel' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `admin-report-${selectedTimeframe}-${new Date().toISOString().split('T')[0]}.xls`;
      a.click();
      window.URL.revokeObjectURL(url);
      console.log('Excel export completed successfully');
    } catch (error) {
      console.error('Excel export error:', error);
      alert('Failed to export Excel file. Please try again.');
    }
  };

  const exportToPDF = (data) => {
    try {
      // For now, show a message about PDF export
      alert('PDF export functionality requires additional libraries. Exporting as CSV instead.');
      exportToCSV(data);
    } catch (error) {
      console.error('PDF export error:', error);
      alert('Failed to export PDF. Please try again.');
    }
  };

  const convertToCSV = (data) => {
    try {
      // Simple CSV conversion with comprehensive data
      const lines = [];
      lines.push('Metric,Value');
      lines.push(`Timeframe,${data.timeframe}`);
      lines.push(`Generated At,${data.generatedAt}`);
      lines.push('');
      lines.push('Ticket Metrics');
      lines.push(`Total Tickets,${data.data.ticketMetrics.total}`);
      lines.push(`Open Tickets,${data.data.ticketMetrics.open}`);
      lines.push(`In Progress,${data.data.ticketMetrics.inProgress}`);
      lines.push(`Completed,${data.data.ticketMetrics.completed}`);
      lines.push(`Closed,${data.data.ticketMetrics.closed}`);
      lines.push(`Avg Resolution Time,${data.data.ticketMetrics.averageResolutionTime} days`);
      lines.push('');
      lines.push('Property Statistics');
      lines.push(`Total Properties,${data.data.propertyStats.total}`);
      lines.push(`Occupied,${data.data.propertyStats.occupied}`);
      lines.push(`Vacant,${data.data.propertyStats.vacant}`);
      lines.push(`Under Maintenance,${data.data.propertyStats.maintenance}`);
      lines.push(`Average Rent,$${data.data.propertyStats.averageRent}`);
      lines.push('');
      lines.push('Revenue Data');
      if (data.data.revenueData.length > 0) {
        lines.push('Date,Revenue');
        data.data.revenueData.forEach(item => {
          lines.push(`${item.date},$${item.value.toFixed(2)}`);
        });
      } else {
        lines.push('Date,Revenue');
        lines.push('No Data,0');
      }
      lines.push('');
      lines.push('User Growth Data');
      if (data.data.userGrowth.length > 0) {
        lines.push('Date,New Users');
        data.data.userGrowth.forEach(item => {
          lines.push(`${item.date},${item.value}`);
        });
      } else {
        lines.push('Date,New Users');
        lines.push('No Data,0');
      }
      lines.push('');
      lines.push('Top Performers');
      if (data.data.topPerformers.length > 0) {
        lines.push('Provider ID,Completed Tickets,Total Expenses');
        data.data.topPerformers.forEach(provider => {
          lines.push(`${provider.providerId},${provider.completedTickets},$${provider.totalExpenses.toFixed(2)}`);
        });
      } else {
        lines.push('Provider ID,Completed Tickets,Total Expenses');
        lines.push('No Data,0,0');
      }
      
      return lines.join('\n');
    } catch (error) {
      console.error('CSV conversion error:', error);
      return 'Error converting data to CSV';
    }
  };

  // Chart configurations
  const userGrowthChartData = {
    labels: reportData.userGrowth.length > 0 
      ? reportData.userGrowth.map(item => {
          try {
            const date = new Date(item.date);
            return isNaN(date.getTime()) ? 'Invalid Date' : date.toLocaleDateString();
          } catch (error) {
            console.warn('Error formatting date for user growth chart:', error);
            return 'Invalid Date';
          }
        })
      : ['No Data'],
    datasets: [{
      label: 'New Users',
      data: reportData.userGrowth.length > 0 
        ? reportData.userGrowth.map(item => item.value)
        : [0],
      borderColor: 'rgb(59, 130, 246)',
      backgroundColor: 'rgba(59, 130, 246, 0.1)',
      tension: 0.1
    }]
  };

  const revenueChartData = {
    labels: reportData.revenueData.length > 0 
      ? reportData.revenueData.map(item => {
          try {
            const date = new Date(item.date);
            return isNaN(date.getTime()) ? 'Invalid Date' : date.toLocaleDateString();
          } catch (error) {
            console.warn('Error formatting date for revenue chart:', error);
            return 'Invalid Date';
          }
        })
      : ['No Data'],
    datasets: [{
      label: 'Revenue ($)',
      data: reportData.revenueData.length > 0 
        ? reportData.revenueData.map(item => item.value)
        : [0],
      borderColor: 'rgb(34, 197, 94)',
      backgroundColor: 'rgba(34, 197, 94, 0.1)',
      tension: 0.1
    }]
  };

  const ticketStatusChartData = {
    labels: ['Open', 'In Progress', 'Completed', 'Closed'],
    datasets: [{
      data: [
        reportData.ticketMetrics.open || 0,
        reportData.ticketMetrics.inProgress || 0,
        reportData.ticketMetrics.completed || 0,
        reportData.ticketMetrics.closed || 0
      ],
      backgroundColor: [
        'rgba(59, 130, 246, 0.8)',
        'rgba(245, 158, 11, 0.8)',
        'rgba(34, 197, 94, 0.8)',
        'rgba(107, 114, 128, 0.8)'
      ],
      borderColor: [
        'rgb(59, 130, 246)',
        'rgb(245, 158, 11)',
        'rgb(34, 197, 94)',
        'rgb(107, 114, 128)'
      ],
      borderWidth: 1
    }]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Platform Analytics'
      }
    },
    scales: {
      y: {
        beginAtZero: true
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading reports...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="flex">
        <Sidebar />
        <div className="flex-1 ml-72 -ml-8">
          <header className="bg-white border-b border-gray-200 sticky top-0 z-10 px-8 py-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-800">Reports</h1>
                <p className="text-sm text-gray-500 mt-0.5">Analytics and platform metrics</p>
              </div>
              <div className="text-sm text-gray-500">
                {new Date().toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long' })}
              </div>
            </div>
          </header>
          <div className="p-8">
        {/* Debug Information - Only visible in development */}
        {process.env.NODE_ENV === 'development' && (
          <div className="bg-gray-100 rounded-lg p-4 mb-8">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Debug Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <p><strong>Loading State:</strong> {loading ? 'Yes' : 'No'}</p>
                <p><strong>Current Timeframe:</strong> {selectedTimeframe}</p>
                <p><strong>User Growth Data Points:</strong> {reportData.userGrowth.length}</p>
                <p><strong>Revenue Data Points:</strong> {reportData.revenueData.length}</p>
              </div>
              <div>
                <p><strong>Total Tickets:</strong> {reportData.ticketMetrics.total}</p>
                <p><strong>Total Properties:</strong> {reportData.propertyStats.total}</p>
                <p><strong>Top Performers:</strong> {reportData.topPerformers.length}</p>
                <button
                  onClick={loadReportData}
                  className="px-3 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600"
                >
                  Reload Data
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Controls */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="flex gap-4">
              <select
                value={selectedTimeframe}
                onChange={(e) => setSelectedTimeframe(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="week">Last Week</option>
                <option value="month">Last Month</option>
                <option value="quarter">Last Quarter</option>
                <option value="year">Last Year</option>
                <option value="all">All Time</option>
              </select>
              
              <select
                value={exportFormat}
                onChange={(e) => setExportFormat(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="pdf">PDF</option>
                <option value="excel">Excel</option>
                <option value="csv">CSV</option>
              </select>
            </div>
            
            <div className="flex gap-2">
              <button
                onClick={loadReportData}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Refresh
              </button>
              <button
                onClick={exportReport}
                disabled={exporting}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {exporting ? 'Exporting...' : 'Export Report'}
              </button>
            </div>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="p-3 bg-blue-100 rounded-lg">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">User Growth</p>
                <p className="text-2xl font-bold text-gray-900">
                  {reportData.userGrowth && reportData.userGrowth.length > 0 
                    ? reportData.userGrowth[reportData.userGrowth.length - 1].value 
                    : 0}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="p-3 bg-green-100 rounded-lg">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Revenue</p>
                <p className="text-2xl font-bold text-gray-900">
                  ${reportData.revenueData && reportData.revenueData.length > 0 
                    ? reportData.revenueData.reduce((sum, item) => sum + (item.value || 0), 0).toFixed(2)
                    : '0.00'}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="p-3 bg-yellow-100 rounded-lg">
                <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Ticket Resolution</p>
                <p className="text-2xl font-bold text-gray-900">
                  {reportData.ticketMetrics && reportData.ticketMetrics.averageResolutionTime !== undefined 
                    ? reportData.ticketMetrics.averageResolutionTime 
                    : 0} days
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="p-3 bg-purple-100 rounded-lg">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Avg. Rent</p>
                <p className="text-2xl font-bold text-gray-900">
                  ${reportData.propertyStats && reportData.propertyStats.averageRent !== undefined 
                    ? reportData.propertyStats.averageRent 
                    : 0}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Charts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* User Growth Chart */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">User Growth Trend</h3>
            <div className="h-64">
              {reportData.userGrowth && reportData.userGrowth.length > 0 ? (
                <Line data={userGrowthChartData} options={chartOptions} />
              ) : (
                <div className="h-full bg-gray-50 rounded-lg flex items-center justify-center">
                  <p className="text-gray-500">No user growth data available for selected timeframe</p>
                </div>
              )}
            </div>
          </div>

          {/* Revenue Chart */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Revenue Trend</h3>
            <div className="h-64">
              {reportData.revenueData && reportData.revenueData.length > 0 ? (
                <Line data={revenueChartData} options={chartOptions} />
              ) : (
                <div className="h-full bg-gray-50 rounded-lg flex items-center justify-center">
                  <p className="text-gray-500">No revenue data available for selected timeframe</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Ticket Status Chart */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Ticket Status Distribution</h3>
          <div className="h-64 flex items-center justify-center">
            {reportData.ticketMetrics && reportData.ticketMetrics.total > 0 ? (
              <div className="w-64 h-64">
                <Doughnut data={ticketStatusChartData} options={chartOptions} />
              </div>
            ) : (
              <div className="h-full bg-gray-50 rounded-lg flex items-center justify-center">
                <p className="text-gray-500">No tickets available for selected timeframe</p>
              </div>
            )}
          </div>
        </div>

        {/* Detailed Reports Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Ticket Metrics */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Ticket Metrics</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Total Tickets</span>
                <span className="font-semibold">{reportData.ticketMetrics?.total || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Open</span>
                <span className="font-semibold text-blue-600">{reportData.ticketMetrics?.open || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">In Progress</span>
                <span className="font-semibold text-yellow-600">{reportData.ticketMetrics?.inProgress || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Completed</span>
                <span className="font-semibold text-green-600">{reportData.ticketMetrics?.completed || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Closed</span>
                <span className="font-semibold text-gray-600">{reportData.ticketMetrics?.closed || 0}</span>
              </div>
            </div>
          </div>

          {/* Property Statistics */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Property Statistics</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Total Properties</span>
                <span className="font-semibold">{reportData.propertyStats?.total || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Occupied</span>
                <span className="font-semibold text-green-600">{reportData.propertyStats?.occupied || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Vacant</span>
                <span className="font-semibold text-yellow-600">{reportData.propertyStats?.vacant || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Under Maintenance</span>
                <span className="font-semibold text-red-600">{reportData.propertyStats?.maintenance || 0}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Top Performers */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Service Providers</h3>
          <div className="overflow-x-auto">
            {reportData.topPerformers && reportData.topPerformers.length > 0 ? (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Provider ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Completed Tickets</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Expenses</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Performance</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {reportData.topPerformers.map((provider, index) => (
                    <tr key={provider.providerId || index}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {provider.providerId || 'Unknown'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {provider.completedTickets || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        ${(provider.totalExpenses || 0).toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                            <div 
                              className="bg-blue-600 h-2 rounded-full" 
                              style={{ width: `${Math.min(((provider.completedTickets || 0) / 10) * 100, 100)}%` }}
                            ></div>
                          </div>
                          <span className="text-xs text-gray-500">
                            {Math.min(((provider.completedTickets || 0) / 10) * 100, 100).toFixed(0)}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500">No service provider data available for selected timeframe</p>
              </div>
            )}
          </div>
        </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminReports;
